/* eslint-disable */
import { useState, useRef, useLayoutEffect, useMemo, useCallback } from 'react';
import { FixedSizeGrid as Grid } from 'react-window';
import Cards from './cards';
import { ArrowLeftRight } from 'lucide-react';

export default function DraggableImageContainer(props) {
    const {
        firstContainer = { list: [], title: 'Available Items', showSearchBar: true },
        secondContainer = { list: [], title: 'Selected Items', showSearchBar: true },
        getContainersList = () => { }
    } = props;

    const firstContainerList = firstContainer?.list || [];
    const secondContainerList = secondContainer?.list || [];
    const gridWrapperRef = useRef(null);
    const secondGridWrapperRef = useRef(null);
    const [gridWidth, setGridWidth] = useState(0);
    const [gridHeight, setGridHeight] = useState(0);
    const [secondGridWidth, setSecondGridWidth] = useState(0);
    const [secondGridHeight, setSecondGridHeight] = useState(0);
    const [firstContainerSearch, setFirstContainerSearch] = useState('');
    const [secondContainerSearch, setSecondContainerSearch] = useState('');

    const CARD_WIDTH = 175;
    const CARD_HEIGHT = 220;

    useLayoutEffect(() => {
        function resize() {
            if (gridWrapperRef.current) {
                setGridWidth(gridWrapperRef.current.offsetWidth);
                setGridHeight(gridWrapperRef.current.offsetHeight);
            }

            if (secondGridWrapperRef.current) {
                setSecondGridWidth(secondGridWrapperRef.current.offsetWidth);
                setSecondGridHeight(secondGridWrapperRef.current.offsetHeight);
            }
        }

        resize();
        window.addEventListener('resize', resize);
        return () => window.removeEventListener('resize', resize);
    }, []);

    const COLUMN_COUNT = useMemo(() => {
        return gridWidth > 0 ? Math.max(1, Math.floor(gridWidth / CARD_WIDTH)) : 1;
    }, [gridWidth]);

    const getFirstContainerList = useMemo(() => {
        if (!firstContainerSearch) return firstContainerList;
        return firstContainerList.filter((item) =>
            item?.productName?.toLowerCase().includes(firstContainerSearch.toLowerCase())
        );
    }, [firstContainerList, firstContainerSearch]);

    const getSecondContainerList = useMemo(() => {
        if (!secondContainerSearch) return secondContainerList;
        return secondContainerList.filter((item) =>
            item?.productName?.toLowerCase().includes(secondContainerSearch.toLowerCase())
        );
    }, [secondContainerList, secondContainerSearch]);

    const firstRowCount = useMemo(() => {
        return Math.ceil(getFirstContainerList.length / COLUMN_COUNT);
    }, [getFirstContainerList.length, COLUMN_COUNT]);

    const secondRowCount = useMemo(() => {
        return Math.ceil(getSecondContainerList.length / COLUMN_COUNT);
    }, [getSecondContainerList.length, COLUMN_COUNT]);


    const handleLeftDragStart = useCallback((e, draggableItem) => {
        e.dataTransfer.effectAllowed = 'move';
        e.dataTransfer.setData('data', JSON.stringify(draggableItem));
    }, []);

    const handleRightDragStart = useCallback((e, draggableItem) => {
        e.dataTransfer.effectAllowed = 'move';
        e.dataTransfer.setData('data', JSON.stringify(draggableItem));
    }, []);

    const handleRightDrop = useCallback((e) => {
        e.preventDefault();
        try {
            const draggableItem = JSON.parse(e.dataTransfer.getData('data'));
            if (!draggableItem?.id) {
                return;
            }

            const alreadyExists = secondContainerList.some((item) => item.id === draggableItem.id);
            if (alreadyExists) {
                return;
            }

            getContainersList({
                firstContainerList: firstContainerList.filter((item) => item.id !== draggableItem.id),
                secondContainerList: [draggableItem, ...secondContainerList]
            });
        } catch (error) {
            console.error('Error handling drop:', error);
        }
    }, [firstContainerList, secondContainerList, getContainersList]);

    const handleLeftDrop = useCallback((e) => {
        e.preventDefault();
        try {
            const draggableItem = JSON.parse(e.dataTransfer.getData('data'));
            if (!draggableItem?.id) {
                return;
            }

            // Check if already in first container
            const alreadyExists = firstContainerList.some((item) => item.id === draggableItem.id);
            if (alreadyExists) {
                return;
            }

            getContainersList({
                firstContainerList: [draggableItem, ...firstContainerList],
                secondContainerList: secondContainerList.filter((item) => item.id !== draggableItem.id)
            });
        } catch (error) {
            console.error('Error handling drop:', error);
        }
    }, [firstContainerList, secondContainerList, getContainersList]);

    // Click handlers
    const handleLeftClick = useCallback((draggableItem) => {
        if (!draggableItem?.id) return;

        // Check if already in second container
        const alreadyExists = secondContainerList.some((item) => item.id === draggableItem.id);
        if (alreadyExists) {
            return;
        }

        getContainersList({
            firstContainerList: firstContainerList.filter((item) => item.id !== draggableItem.id),
            secondContainerList: [draggableItem, ...secondContainerList]
        });
    }, [firstContainerList, secondContainerList, getContainersList]);

    const handleRightClick = useCallback((draggableItem) => {
        if (!draggableItem?.id) return;

        // Check if already in first container
        const alreadyExists = firstContainerList.some((item) => item.id === draggableItem.id);
        if (alreadyExists) {
            return;
        }

        getContainersList({
            firstContainerList: [draggableItem, ...firstContainerList],
            secondContainerList: secondContainerList.filter((item) => item.id !== draggableItem.id)
        });
    }, [firstContainerList, secondContainerList, getContainersList]);

    // Keyboard navigation
    const handleKeyDown = useCallback((e, item, handler) => {
        if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            handler(item);
        }
    }, []);

    // Cell renderer for first grid - memoized
    const cell = useCallback(({ columnIndex, rowIndex, style }) => {
        const index = rowIndex * COLUMN_COUNT + columnIndex;
        if (index >= getFirstContainerList.length) return null;

        const item = getFirstContainerList[index];
        if (!item) return null;

        return (
            <div
                role="button"
                tabIndex={0}
                onKeyDown={(e) => handleKeyDown(e, item, handleLeftClick)}
                aria-label={`Move ${item.productName} to selected items`}
            >
                <Cards
                    key={item.id}
                    item={item}
                    style={style}
                    handleDragStart={handleLeftDragStart}
                    handleClick={handleLeftClick}
                />
            </div>
        );
    }, [getFirstContainerList, COLUMN_COUNT, handleLeftDragStart, handleLeftClick, handleKeyDown]);

    // Cell renderer for second grid - memoized
    const cell2 = useCallback(({ columnIndex, rowIndex, style }) => {
        const index = rowIndex * COLUMN_COUNT + columnIndex;
        if (index >= getSecondContainerList.length) return null;

        const item = getSecondContainerList[index];
        if (!item) return null;

        return (
            <div
                role="button"
                tabIndex={0}
                onKeyDown={(e) => handleKeyDown(e, item, handleRightClick)}
                aria-label={`Move ${item.productName} to available items`}
            >
                <Cards
                    key={item.id}
                    item={item}
                    style={style}
                    handleDragStart={handleRightDragStart}
                    handleClick={handleRightClick}
                />
            </div>
        );
    }, [getSecondContainerList, COLUMN_COUNT, handleRightDragStart, handleRightClick, handleKeyDown]);

    return (
        <div className='w-full h-full flex items-center gap-4 justify-center relative'>
            <div className='absolute top-4 left-1/2 transform -translate-x-1/2 bg-white text-gray-600 p-[13px] rounded-full shadow-lg z-50'>
                <ArrowLeftRight />
            </div>

            <div
                className='w-1/2 flex flex-col gap-2 h-full justify-start bg-slate-200 rounded-md p-4'
                onDragOver={(e) => e.preventDefault()}
                onDrop={handleLeftDrop}
                role="region"
                aria-label={firstContainer?.title || 'Available Items'}
            >
                <div className='flex items-center justify-between'>
                    <p className='text-[15px] font-light'>
                        {firstContainer?.title ?? 'Available Items'}
                    </p>
                    <span className='text-xs text-gray-600' aria-live="polite">
                        {getFirstContainerList.length} {getFirstContainerList.length === 1 ? 'item' : 'items'}
                    </span>
                </div>

                {firstContainer?.showSearchBar && (
                    <input
                        placeholder={firstContainer?.placeholder ?? 'Search'}
                        value={firstContainerSearch}
                        onChange={(e) => setFirstContainerSearch(e.target.value)}
                        className='w-full bg-transparent border border-gray-500 rounded-md px-4 py-2 
                        text-gray-700 placeholder-gray-400 
                        focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500'
                        aria-label="Search available items"
                    />
                )}

                <div ref={gridWrapperRef} className='w-full flex-1 overflow-hidden'>
                    {gridWidth > 0 && getFirstContainerList.length > 0 ? (
                        <Grid
                            columnCount={COLUMN_COUNT}
                            columnWidth={CARD_WIDTH}
                            height={gridHeight}
                            rowCount={firstRowCount}
                            rowHeight={CARD_HEIGHT}
                            width={gridWidth}
                        >
                            {cell}
                        </Grid>
                    ) : (
                        <div className='w-full h-full flex items-center justify-center text-gray-500'>
                            {firstContainerSearch ? 'No items match your search' : 'No items available'}
                        </div>
                    )}
                </div>
            </div>

            <div
                className='w-1/2 flex flex-col gap-2 h-full justify-start bg-[#DCFCE7] rounded-md p-4'
                onDragOver={(e) => e.preventDefault()}
                onDrop={handleRightDrop}
                role="region"
                aria-label={secondContainer?.title || 'Selected Items'}
            >
                <div className='flex items-center justify-between'>
                    <p className='text-[15px] font-light'>
                        {secondContainer?.title ?? 'Selected Items'}
                    </p>
                    <span className='text-xs text-gray-600' aria-live="polite">
                        {getSecondContainerList.length} {getSecondContainerList.length === 1 ? 'item' : 'items'}
                    </span>
                </div>

                {secondContainer?.showSearchBar && (
                    <input
                        placeholder={secondContainer?.placeholder ?? 'Search'}
                        value={secondContainerSearch}
                        onChange={(e) => setSecondContainerSearch(e.target.value)}
                        className='w-full bg-transparent border border-gray-500 rounded-md px-4 py-2 
                        text-gray-700 placeholder-gray-400 
                        focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500'
                        aria-label="Search selected items"
                    />
                )}

                <div ref={secondGridWrapperRef} className='w-full flex-1 overflow-hidden'>
                    {secondGridWidth > 0 && getSecondContainerList.length > 0 ? (
                        <Grid
                            columnCount={COLUMN_COUNT}
                            columnWidth={CARD_WIDTH}
                            height={secondGridHeight}
                            rowCount={secondRowCount}
                            rowHeight={CARD_HEIGHT}
                            width={secondGridWidth}
                        >
                            {cell2}
                        </Grid>
                    ) : (
                        <div className='w-full h-full flex items-center justify-center text-gray-600'>
                            {secondContainerSearch ? 'No items match your search' : 'Drag items here or click to select'}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}